import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, Users, FolderKanban, Palette, LogOut, FileSpreadsheet, GanttChart, UsersRound, Plug, Target, Receipt, ShieldCheck, Calendar, Clock, Building, FolderOpen } from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import CommandPalette from "./CommandPalette";
import { useEffect, useState } from "react";

export default function AppLayout() {
  const { user, brand, logout } = useAuth();
  const nav = useNavigate();
  const [redCount, setRedCount] = useState(0);

  useEffect(() => {
    const load = () => api.get("/dashboard/stats").then((r) => setRedCount(r.data.projects_red || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const items = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, badge: redCount },
    { to: "/opportunities", icon: Target, label: "Opportunità" },
    { to: "/quotes", icon: Receipt, label: "Preventivi" },
    { to: "/agenda", icon: Calendar, label: "Agenda" },
    { to: "/timesheet", icon: Clock, label: "Timesheet" },
    { to: "/timeline", icon: GanttChart, label: "Timeline" },
    { to: "/clients", icon: Users, label: "Clienti" },
    { to: "/projects", icon: FolderKanban, label: "Gare & Progetti" },
    { to: "/pratiche", icon: Building, label: "Pratiche Edilizie" },
    { to: "/documents", icon: FolderOpen, label: "Documenti" },
    { to: "/adempimenti", icon: ShieldCheck, label: "Adempimenti" },
    { to: "/team", icon: UsersRound, label: "Team" },
    { to: "/brand", icon: Palette, label: "Brand" },
    { to: "/integrations", icon: Plug, label: "Integrazioni" },
  ];

  const downloadReport = async () => {
    try {
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/reports/gare.xlsx`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("errore");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cruscotto_gare_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report scaricato");
    } catch (e) { toast.error("Errore download report"); }
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAF7]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-neutral-200 bg-white flex flex-col">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            {brand?.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 flex items-center justify-center brand-bg text-white font-display font-bold text-lg">
                {brand?.studio_name?.[0] || "S"}
              </div>
            )}
            <div>
              <div className="font-display font-semibold text-neutral-900 leading-tight text-[15px]" data-testid="sidebar-studio-name">
                {brand?.studio_name || "amdirDesk"}
              </div>
              <div className="mono-label mt-0.5">CRM</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {items.map(({ to, icon: Icon, label, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 ${
                  isActive
                    ? "brand-text bg-neutral-50 border-l-current font-medium"
                    : "text-neutral-600 border-transparent hover:bg-neutral-50"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span data-testid="nav-badge-red" className="text-[10px] font-mono font-semibold px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-200">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
          <button
            onClick={downloadReport}
            data-testid="nav-download-report"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 border-transparent text-neutral-600 hover:bg-neutral-50"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Export Excel
          </button>
        </nav>

        <div className="p-3 border-t border-neutral-200">
          <div className="px-3 py-2 flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} className="h-8 w-8 rounded-full" alt="me" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-neutral-200 flex items-center justify-center text-xs font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="p-1.5 hover:bg-neutral-100 text-neutral-500">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto relative">
        {/* Top bar with global search — inline, no overlap */}
        <div className="sticky top-0 z-30 bg-[#FAFAF7]/90 backdrop-blur border-b border-neutral-200 px-8 lg:px-10 py-3 flex items-center justify-end">
          <CommandPalette inline />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
